/**
 * DoctorWorkspace.jsx — DOCTOR-WORKSPACE-001
 * ============================================================
 *
 * Dense, grid-first operational workspace for the provider_doctor
 * role (and read-only-supervisory for provider_admin).
 *
 * Visual authority: COORDINATOR-GRID-UX-001 — reuses the same
 * `journey-ux` primitives (DataGrid, FilterChips, DetailDrawer,
 * BulkBar, Pill). NO cards, NO dashboard tiles.
 *
 * Data: /api/doctor/workspace/* (filtered lens over the canonical
 * Materialization domain — no new authorities, no new collections).
 *
 * Six views (Coordinator parity, doctor-scoped column subset):
 *   1. Cases (My Patients Grid)         · FilterChips with bucket counts
 *   2. Requirements (Doctor-relevant)
 *   3. Attachments (DOCUMENT rows)
 *   4. Consents (CONSENT rows)
 *   5. History (patient_case_plan_revisions for doctor's cases)
 *   6. (Dashboard counts live in the sticky chip row + summary tab counts.)
 *
 * Clinical actions (in DetailDrawer footer for requirement rows):
 *   - Mark reviewed   → POST /cases/{case_id}/rows/{row_id}/review {action:'reviewed'}
 *   - Request update  → action:'request_update'
 *   - Escalate        → action:'escalate'
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Stethoscope, RefreshCw, ExternalLink, CheckCircle2, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../../utils/api';
// M16B-07B — canonical Treatment Ready visibility (per-treatment-slug)
import TreatmentReadyPanel from '../../components/treatment-ready/TreatmentReadyPanel';
import {
  DataGrid,
  DetailDrawer,
  DrawerField,
  FilterChips,
  Pill,
} from '../../components/journey-ux';

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */
const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
  } catch {
    return '—';
  }
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return '—'; }
};
const numPill = (n, tone = 'rose') =>
  n > 0 ? <Pill tone={tone}>{n}</Pill> : <span className="text-slate-300">—</span>;

const KIND_TONE = { REQUIREMENT: 'sky', TASK: 'violet' };
const STATUS_TONE = {
  intake: 'sky', active: 'sky', in_progress: 'amber', on_track: 'emerald',
  blocked: 'rose', completed: 'emerald', canceled: 'slate',
};

/* -------------------------------------------------------------------- */
/*  View definitions                                                    */
/* -------------------------------------------------------------------- */
const VIEWS = [
  { id: 'cases',        label: 'Cases',        path: 'cases' },
  { id: 'requirements', label: 'Requirements', path: 'requirements' },
  { id: 'attachments',  label: 'Attachments',  path: 'attachments' },
  { id: 'consents',     label: 'Consents',     path: 'consents' },
  { id: 'reviews',      label: 'Clinical Reviews', path: 'clinical-reviews' },
  { id: 'history',      label: 'History',      path: 'history' },
];

const BUCKETS = [
  { id: 'all',            label: 'All' },
  { id: 'due_today',      label: 'Due today' },
  { id: 'overdue',        label: 'Overdue requirements' },
  { id: 'waiting_review', label: 'Waiting review' },
  { id: 'completed',      label: 'Completed' },
  { id: 'needs_action',   label: 'Needs action' },
];

/* -------------------------------------------------------------------- */
/*  Column factories                                                    */
/* -------------------------------------------------------------------- */
const casesColumns = (navigate) => [
  { id: 'case',    header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => (
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{r.country || '—'}</span>
        <span className="font-medium text-slate-800 truncate">{r.patient_name || '—'}</span>
      </div>
    ) },
  { id: 'treatment', header: 'Treatment',
    render: (r) => <span className="text-slate-700 truncate">{r.treatment_name || r.treatment_slug || '—'}</span> },
  { id: 'journey', header: 'Journey Step',
    render: (r) => r.journey_step ? <Pill tone="sky">{r.journey_step}</Pill> : <span className="text-slate-300">—</span> },
  { id: 'assigned', header: 'Assigned', align: 'right',
    render: (r) => <span className="font-semibold text-slate-700">{r.assigned}</span> },
  { id: 'due_today', header: 'Due today', align: 'right',
    render: (r) => numPill(r.due_today, 'amber') },
  { id: 'overdue',   header: 'Overdue',   align: 'right',
    render: (r) => numPill(r.overdue, 'rose') },
  { id: 'waiting',   header: 'Waiting',   align: 'right',
    render: (r) => numPill(r.waiting_review, 'rose') },
  { id: 'completed', header: 'Completed', align: 'right',
    render: (r) => numPill(r.completed_recently, 'emerald') },
  { id: 'status',    header: 'Status',
    render: (r) => <Pill tone={STATUS_TONE[(r.status || '').toLowerCase()] || 'slate'}>{r.status || '—'}</Pill> },
  { id: 'coordinator', header: 'Coordinator',
    render: (r) => <span className="text-slate-500 font-mono text-[10.5px]">{(r.assigned_coordinator_id || '—').slice(0, 12)}</span> },
  { id: 'last_activity', header: 'Last activity',
    render: (r) => <span className="text-slate-500">{fmtRel(r.last_activity)}</span> },
  { id: 'open', header: '',
    render: (r) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); navigate(`/clinic/cases/${r.case_id}/workflow`); }}
        className="text-sky-700 hover:underline text-[11px] inline-flex items-center gap-1"
        data-testid={`doc-open-case-${r.case_id}`}
      >
        Open <ExternalLink className="h-3 w-3" />
      </button>
    ),
    width: 'w-16',
  },
];

const requirementsColumns = () => [
  { id: 'case',    header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase text-slate-500">{r.country || '—'}</span>
        <span className="text-slate-800 truncate">{r.patient_name || '—'}</span>
      </div>
    ) },
  { id: 'title', header: 'Requirement', render: (r) => <span className="text-slate-800 truncate">{r.title}</span> },
  { id: 'kind', header: 'Kind',
    render: (r) => <Pill tone={KIND_TONE[r.kind] || 'slate'}>{r.requirement_type || r.kind}</Pill> },
  { id: 'role', header: 'Owner',
    render: (r) => <span className="text-slate-600">{r.default_role || '—'}</span> },
  { id: 'due', header: 'Due',
    render: (r) => r.due_at
      ? <span className={r.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}>{fmtDate(r.due_at)}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'status', header: 'Status',
    render: (r) => r.satisfaction_status === 'satisfied'
      ? <Pill tone="emerald">Satisfied</Pill>
      : r.is_overdue ? <Pill tone="rose">Overdue</Pill> : <Pill tone="amber">Pending</Pill> },
  { id: 'via', header: 'Via',
    render: (r) => r.satisfied_via ? <Pill tone="slate">{r.satisfied_via}</Pill> : <span className="text-slate-300">—</span> },
  { id: 'when', header: 'Satisfied',
    render: (r) => r.satisfied_at ? <span className="text-slate-500">{fmtRel(r.satisfied_at)}</span> : <span className="text-slate-300">—</span> },
];

const attachmentsColumns = () => [
  { id: 'case',    header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => <span className="text-slate-800 truncate">{r.patient_name || '—'}</span> },
  { id: 'requirement', header: 'Requirement',
    render: (r) => <span className="text-slate-800 truncate">{r.requirement_title || '—'}</span> },
  { id: 'doc', header: 'Doc',
    render: (r) => r.legal_document_id
      ? <span className="font-mono text-[10.5px] text-slate-600">{r.legal_document_id.slice(0, 12)} v{r.legal_document_version || '—'}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'file', header: 'File',
    render: (r) => <span className="text-slate-700 truncate">{r.file_name || '—'}</span> },
  { id: 'submitted', header: 'Submitted',
    render: (r) => r.submitted_at ? <span className="text-slate-500">{fmtRel(r.submitted_at)}</span> : <span className="text-slate-300">—</span> },
  { id: 'review', header: 'Review',
    render: (r) => {
      const tone = r.review_state === 'reviewed' ? 'emerald' : r.review_state === 'submitted' ? 'amber' : 'slate';
      return <Pill tone={tone}>{r.review_state}</Pill>;
    } },
  { id: 'status', header: 'Status',
    render: (r) => r.satisfaction_status === 'satisfied' ? <Pill tone="emerald">Satisfied</Pill> : <Pill tone="amber">Pending</Pill> },
];

const consentsColumns = () => [
  { id: 'case',    header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'patient', header: 'Patient',
    render: (r) => <span className="text-slate-800 truncate">{r.patient_name || '—'}</span> },
  { id: 'requirement', header: 'Consent',
    render: (r) => <span className="text-slate-800 truncate">{r.requirement_title || '—'}</span> },
  { id: 'key', header: 'Content',
    render: (r) => r.consent_content_key
      ? <span className="font-mono text-[10.5px] text-slate-600">{r.consent_content_key} v{r.consent_content_version || '—'}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'consent_id', header: 'Consent ID',
    render: (r) => r.consent_id ? <span className="font-mono text-[10.5px] text-slate-600">{r.consent_id.slice(0, 14)}</span> : <span className="text-slate-300">—</span> },
  { id: 'submitted', header: 'Submitted',
    render: (r) => r.submitted_at ? <span className="text-slate-500">{fmtRel(r.submitted_at)}</span> : <span className="text-slate-300">—</span> },
  { id: 'status', header: 'Status',
    render: (r) => r.satisfaction_status === 'satisfied' ? <Pill tone="emerald">Signed</Pill> : <Pill tone="amber">Pending</Pill> },
];

const historyColumns = () => [
  { id: 'case',    header: 'Case',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24' },
  { id: 'action', header: 'Action',
    render: (r) => (
      <Pill tone={
        r.action?.startsWith('doctor_review:reviewed') ? 'emerald'
        : r.action?.startsWith('doctor_review:request_update') ? 'amber'
        : r.action?.startsWith('doctor_review:escalate') ? 'rose'
        : r.action?.startsWith('override-undo') ? 'slate'
        : r.action?.startsWith('override') ? 'violet'
        : r.action === 'rematerialize' ? 'amber'
        : 'sky'
      }>{r.action || '—'}</Pill>
    ) },
  { id: 'actor', header: 'Actor',
    render: (r) => <span className="text-slate-600 font-mono text-[10.5px]">{(r.actor || '').slice(0, 18)}</span> },
  { id: 'detail', header: 'Detail',
    render: (r) => (
      <span className="font-mono text-[10.5px] text-slate-500 truncate" title={JSON.stringify(r.after || {})}>
        {JSON.stringify(r.after || {}).slice(0, 80)}
      </span>
    ) },
  { id: 'when', header: 'When', render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
];

const ROW_KEY_FOR = {
  cases:        (r) => r.case_id,
  requirements: (r) => `${r.case_id}::${r.row_id}`,
  attachments:  (r) => `${r.case_id}::${r.row_id}`,
  consents:     (r) => `${r.case_id}::${r.row_id}`,
  history:      (r) => r.revision_id,
};

/* -------------------------------------------------------------------- */
/*  Main component                                                      */
/* -------------------------------------------------------------------- */
export default function DoctorWorkspace() {
  const navigate = useNavigate();
  const [view, setView] = useState('cases');
  const [bucket, setBucket] = useState('all');
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [reviewing, setReviewing] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/doctor/workspace/summary');
      if (data?.success) setSummary(data);
    } catch { setSummary(null); }
  }, []);

  const fetchView = useCallback(async () => {
    if (view === 'reviews') { setItems([]); setLoading(false); setActiveRow(null); return; }
    setLoading(true);
    setActiveRow(null);
    setItems([]); // clear stale rows from the prior view to avoid key collisions during the swap
    try {
      const path = VIEWS.find((v) => v.id === view)?.path;
      const query = view === 'cases' && bucket !== 'all' ? `?bucket=${bucket}` : '';
      const { data } = await api.get(`/doctor/workspace/${path}${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [view, bucket]);

  useEffect(() => {
    fetchSummary();
    fetchView();
  }, [fetchSummary, fetchView, reloadTick]);

  const columns = useMemo(() => {
    if (view === 'cases')        return casesColumns(navigate);
    if (view === 'requirements') return requirementsColumns();
    if (view === 'attachments')  return attachmentsColumns();
    if (view === 'consents')     return consentsColumns();
    return historyColumns();
  }, [view, navigate]);

  const chips = useMemo(() => {
    if (view !== 'cases') return [];
    return BUCKETS.map((c) => ({
      ...c,
      count: c.id === 'all' ? (summary?.buckets?.assigned ?? 0) : (summary?.buckets?.[c.id] ?? 0),
    }));
  }, [summary, view]);

  const rowKey = ROW_KEY_FOR[view];

  /* ---------------------- review action handler --------------------- */
  const submitReview = useCallback(async (action) => {
    if (!activeRow || !activeRow.case_id || !activeRow.row_id) return;
    setReviewing(true);
    try {
      const { data } = await api.post(
        `/doctor/workspace/cases/${activeRow.case_id}/rows/${activeRow.row_id}/review`,
        { action },
      );
      if (data?.success) {
        toast.success(`Action saved: ${action}`);
        setActiveRow(null);
        setReloadTick((t) => t + 1);
      } else {
        toast.error(data?.message || 'Review failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Review failed');
    } finally {
      setReviewing(false);
    }
  }, [activeRow]);

  return (
    <div data-testid="doctor-workspace" className="min-h-[calc(100vh-4rem)] bg-slate-50 flex flex-col">
      {/* HEADER — sticky, compact */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20" data-testid="doc-header">
        <div className="px-5 py-2 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Stethoscope className="h-4 w-4 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Doctor Workspace</div>
              <div className="text-[13px] font-semibold text-slate-800 truncate" data-testid="doc-clinic-name">
                Clinic · {summary?.clinic_id || '—'} {summary?.is_doctor === false && (
                  <span className="ml-2 text-[10px] uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">read-only</span>
                )}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
            <span data-testid="doc-id">{summary?.doctor_id || '—'}</span>
            <button
              type="button"
              onClick={() => setReloadTick((t) => t + 1)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 text-slate-700"
              data-testid="doc-refresh-btn"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        <nav className="px-5 border-t border-slate-100 flex items-center gap-0 overflow-x-auto" data-testid="doc-view-tabs">
          {VIEWS.map((v) => {
            const active = v.id === view;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                data-testid={`doc-tab-${v.id}`}
                className={`text-[12px] px-3 py-2 border-b-2 transition-colors -mb-px ${
                  active ? 'border-slate-900 text-slate-900 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {v.label}
                {v.id === 'cases' && summary?.buckets && (
                  <span className="ml-1.5 text-[10px] text-slate-400">{summary.buckets.assigned}</span>
                )}
                {v.id === 'requirements' && summary?.buckets?.needs_action > 0 && (
                  <span className="ml-1.5 text-[10px] text-rose-500">{summary.buckets.needs_action}</span>
                )}
              </button>
            );
          })}
        </nav>
      </header>

      {/* FILTER CHIPS or summary strip */}
      {view === 'cases' ? (
        <FilterChips
          chips={chips}
          activeId={bucket}
          onChange={setBucket}
          testIdPrefix="doc-filter-chip"
          resultLabel={`${items.length} of ${summary?.buckets?.assigned ?? 0} case(s)`}
        />
      ) : view === 'reviews' ? null : (
        <div className="px-5 py-2 border-b border-slate-100 bg-white flex items-center gap-2 text-[11px] text-slate-500">
          <span>{items.length} row(s)</span>
          {summary?.buckets && (
            <>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.assigned} case(s) assigned</span>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.overdue} overdue</span>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.waiting_review} waiting review</span>
            </>
          )}
        </div>
      )}

      {/* GRID + DRAWER */}
      {view === 'reviews' ? (
        <DoctorClinicalReviewsPanel />
      ) : (
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0">
          <DataGrid
            testId="doc"
            columns={columns}
            rows={items}
            rowKey={rowKey}
            activeRowId={activeRow ? rowKey(activeRow) : null}
            onRowClick={setActiveRow}
            loading={loading}
            loadingMessage="Loading workspace…"
            emptyMessage={
              view === 'cases'        ? 'No cases assigned to you.'
              : view === 'requirements' ? 'No doctor-relevant requirements.'
              : view === 'attachments' ? 'No documents on your cases.'
              : view === 'consents'    ? 'No consents on your cases.'
              : 'No revisions yet.'
            }
            selectable={false}
            maxHeightClass="h-[calc(100vh-220px)]"
          />
        </div>

        {activeRow && (
          <DoctorDrawer
            view={view}
            row={activeRow}
            onClose={() => setActiveRow(null)}
            onJumpToCase={(cid) => navigate(`/clinic/cases/${cid}/workflow`)}
            onReview={submitReview}
            reviewing={reviewing}
            isDoctor={!!summary?.is_doctor}
          />
        )}
      </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Drawer per view                                                     */
/* -------------------------------------------------------------------- */
function DoctorDrawer({ view, row, onClose, onJumpToCase, onReview, reviewing, isDoctor }) {
  const title =
    view === 'cases'        ? `${row.patient_name} · ${row.treatment_name || row.treatment_slug || ''}`
    : view === 'requirements' ? row.title
    : view === 'attachments' ? (row.requirement_title || row.file_name || row.row_id)
    : view === 'consents'    ? (row.requirement_title || row.consent_content_key || row.row_id)
    : (row.action || 'Revision');
  const eyebrow =
    view === 'cases'        ? `Case #${(row.case_id || '').slice(-6)}`
    : view === 'requirements' ? `Requirement · #${(row.case_id || '').slice(-6)}`
    : view === 'attachments' ? `Attachment · #${(row.case_id || '').slice(-6)}`
    : view === 'consents'    ? `Consent · #${(row.case_id || '').slice(-6)}`
    : `Revision · #${(row.case_id || '').slice(-6)}`;

  const showReviewActions = (
    isDoctor
    && (view === 'requirements' || view === 'attachments' || view === 'consents')
    && row.satisfaction_status !== 'satisfied'
  );

  const footer = (
    <div className="space-y-2">
      {showReviewActions && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="doc-drawer-actions">
          <button
            type="button"
            disabled={reviewing}
            onClick={() => onReview('reviewed')}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="doc-action-reviewed"
          >
            <CheckCircle2 className="h-3 w-3" /> Mark reviewed
          </button>
          <button
            type="button"
            disabled={reviewing}
            onClick={() => onReview('request_update')}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            data-testid="doc-action-request-update"
          >
            <RotateCcw className="h-3 w-3" /> Request update
          </button>
          <button
            type="button"
            disabled={reviewing}
            onClick={() => onReview('escalate')}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            data-testid="doc-action-escalate"
          >
            <AlertTriangle className="h-3 w-3" /> Escalate
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] text-slate-500 font-mono truncate">{row.row_id || row.case_id || ''}</span>
        {row.case_id && (
          <button
            type="button"
            onClick={() => onJumpToCase(row.case_id)}
            className="text-[11px] px-2 py-1 bg-slate-900 text-white rounded hover:bg-slate-800 inline-flex items-center gap-1"
            data-testid="doc-drawer-open-case"
          >
            Open case <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <DetailDrawer open onClose={onClose} title={title} eyebrow={eyebrow} footer={footer} testId="doc-drawer">
      {view === 'cases' && (
        <>
          <DrawerField label="Patient"       value={row.patient_name || '—'} />
          <DrawerField label="Country"       value={row.country || '—'} />
          <DrawerField label="Treatment"     value={row.treatment_name || row.treatment_slug || '—'} />
          <DrawerField label="Journey step"  value={row.journey_step || '—'} />
          <DrawerField label="Status"        value={row.status || '—'} />
          <DrawerField label="Doctor rows"   value={row.assigned} />
          <DrawerField label="Due today"     value={row.due_today} />
          <DrawerField label="Overdue"       value={row.overdue} />
          <DrawerField label="Waiting review" value={row.waiting_review} />
          <DrawerField label="Completed (14d)" value={row.completed_recently} />
          <DrawerField label="Coordinator"   value={row.assigned_coordinator_id || '—'} />
          <DrawerField label="Doctor"        value={row.assigned_doctor_id || '—'} />
          <DrawerField label="Last activity" value={fmtRel(row.last_activity)} />
        </>
      )}
      {view === 'requirements' && (
        <>
          <DrawerField label="Case"    value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Patient" value={row.patient_name || '—'} />
          <DrawerField label="Kind"    value={row.requirement_type || row.kind} />
          <DrawerField label="Owner"   value={row.default_role || '—'} />
          <DrawerField label="Source"  value={row.source || '—'} />
          <DrawerField label="Status"  value={row.satisfaction_status || '—'} />
          <DrawerField label="Due"     value={fmtDate(row.due_at)} />
          {row.satisfied_at && <DrawerField label="Satisfied" value={fmtRel(row.satisfied_at)} />}
          {row.satisfied_via && <DrawerField label="Via"       value={row.satisfied_via} />}
          {row.satisfied_by  && <DrawerField label="By"        value={row.satisfied_by} />}
          {row.legal_document_id && <DrawerField label="Legal doc" value={`${row.legal_document_id} v${row.legal_document_version || '—'}`} />}
          {row.consent_content_key && <DrawerField label="Consent"  value={`${row.consent_content_key} v${row.consent_content_version || '—'}`} />}
          {row.requirement_id && <DrawerField label="Requirement ID" value={row.requirement_id} />}
        </>
      )}
      {view === 'attachments' && (
        <>
          <DrawerField label="Case"        value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Patient"     value={row.patient_name || '—'} />
          <DrawerField label="Requirement" value={row.requirement_title || '—'} />
          <DrawerField label="Legal doc"   value={row.legal_document_id ? `${row.legal_document_id} v${row.legal_document_version || '—'}` : '—'} />
          <DrawerField label="File"        value={row.file_name || '—'} />
          <DrawerField label="Submitted"   value={fmtRel(row.submitted_at)} />
          <DrawerField label="Submitted by" value={row.submitted_by || '—'} />
          <DrawerField label="Review state" value={row.review_state || '—'} />
          <DrawerField label="Status"      value={row.satisfaction_status || '—'} />
          {row.requirement_id && <DrawerField label="Requirement ID" value={row.requirement_id} />}
        </>
      )}
      {view === 'consents' && (
        <>
          <DrawerField label="Case"        value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Patient"     value={row.patient_name || '—'} />
          <DrawerField label="Consent"     value={row.requirement_title || '—'} />
          <DrawerField label="Content key" value={row.consent_content_key ? `${row.consent_content_key} v${row.consent_content_version || '—'}` : '—'} />
          <DrawerField label="Consent ID"  value={row.consent_id || '—'} />
          <DrawerField label="Submitted"   value={fmtRel(row.submitted_at)} />
          <DrawerField label="Status"      value={row.satisfaction_status || '—'} />
          {row.requirement_id && <DrawerField label="Requirement ID" value={row.requirement_id} />}
        </>
      )}
      {view === 'history' && (
        <>
          <DrawerField label="Case"   value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label="Action" value={row.action || '—'} />
          <DrawerField label="Actor"  value={row.actor || '—'} />
          <DrawerField label="When"   value={fmtRel(row.created_at)} />
          {row.before && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Before</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">{JSON.stringify(row.before, null, 2)}</pre>
            </div>
          )}
          {row.after && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">After</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">{JSON.stringify(row.after, null, 2)}</pre>
            </div>
          )}
        </>
      )}
    </DetailDrawer>
  );
}


/* -------------------------------------------------------------------- */
/*  M16B-06A — Doctor Clinical Reviews panel (assigned + submit)        */
/* -------------------------------------------------------------------- */
const REC_OPTIONS = [
  { id: 'suitable',                 label: 'Suitable' },
  { id: 'suitable_with_conditions', label: 'Suitable with conditions' },
  { id: 'needs_more_information',   label: 'Needs more information' },
  { id: 'not_suitable',             label: 'Not suitable' },
];

function DoctorClinicalReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive]   = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/doctor/clinical-reviews/assigned');
      setReviews(data?.reviews || []);
    } catch { setReviews([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload, reloadTick]);

  return (
    <div className="flex flex-1 min-h-0" data-testid="doctor-clinical-reviews">
      <div className="flex-1 min-w-0 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : reviews.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="doctor-clinical-reviews-empty">No clinical reviews assigned to you.</div>
        ) : (
          reviews.map(r => (
            <button
              key={r.clinical_review_id}
              onClick={() => setActive(r)}
              className={`w-full text-left border rounded-lg p-3 hover:border-slate-400 transition-colors ${active?.clinical_review_id === r.clinical_review_id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white'}`}
              data-testid={`doctor-review-row-${r.clinical_review_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">
                  Lead #{(r.lead_id || '').slice(-6)} · Review #{r.review_no}
                </div>
                <Pill tone={r.status === 'requested' ? 'amber' : r.status === 'submitted' ? 'emerald' : 'slate'}>{r.status}</Pill>
              </div>
              <div className="mt-1 text-[12px] text-slate-600">
                Initial: <span className="font-mono">{r.initial_requested_treatment || '—'}</span>
                {r.request_reason ? <span className="ml-2 text-slate-500">· {r.request_reason}</span> : null}
              </div>
            </button>
          ))
        )}
      </div>
      {active && (
        <DoctorClinicalReviewForm
          review={active}
          onClose={() => setActive(null)}
          onSubmitted={() => { setActive(null); setReloadTick(t => t + 1); }}
        />
      )}
    </div>
  );
}

function DoctorClinicalReviewForm({ review, onClose, onSubmitted }) {
  const editable = review.status === 'requested';
  const [rec, setRec]           = useState(review.recommendation || 'suitable_with_conditions');
  const [recommended, setRecommended] = useState((review.recommended_treatments || []).map(t => t.slug));
  const [additional,  setAdditional]  = useState((review.additional_treatments  || []).map(t => t.slug));
  const [alternative, setAlternative] = useState((review.alternative_treatments || []).map(t => t.slug));
  const [removed,     setRemoved]     = useState((review.removed_or_rejected_treatments || []).map(t => t.slug));
  const [notes,       setNotes]       = useState(review.doctor_notes || '');
  const [patientSummary, setPatientSummary] = useState(review.patient_visible_summary || '');
  const [guidance,    setGuidance]    = useState(review.sales_rep_guidance || '');
  const [submitting,  setSubmitting]  = useState(false);

  // Canonical catalog — single fetch, shared across all four pickers.
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    api.get('/public/catalog/treatments')
      .then(({ data }) => setCatalog(data?.treatments || []))
      .catch(() => setCatalog([]));
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/clinic/clinical-reviews/${review.clinical_review_id}/submit`, {
        recommendation:                rec,
        recommended_treatments:        recommended.map(slug => ({ slug })),
        additional_treatments:         additional.map(slug => ({ slug })),
        alternative_treatments:        alternative.map(slug => ({ slug })),
        removed_or_rejected_treatments: removed.map(slug => ({ slug })),
        doctor_notes:                  notes,
        patient_visible_summary:       patientSummary,
        sales_rep_guidance:            guidance,
        required_follow_up:            [],
        required_documents:            [],
        required_profile_fields:       [],
      });
      if (data?.success) {
        toast.success(`Clinical review submitted: ${rec}`);
        onSubmitted();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-[420px] border-l border-slate-200 bg-white flex flex-col" data-testid="doctor-review-form-drawer">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">Review #{review.review_no}</div>
          <div className="text-[11px] text-slate-500 font-mono">{review.clinical_review_id}</div>
        </div>
        <button onClick={onClose} className="text-slate-500 text-sm" data-testid="doctor-review-form-close">Close</button>
      </div>
      <div className="px-4 py-3 space-y-3 overflow-y-auto flex-1 text-sm">
        {/* M16B-07B — Treatment Ready (read-only, clinic-evidence, no commercial info) */}
        {review.lead_id && (
          <TreatmentReadyPanel
            leadId={review.lead_id}
            mode="clinic"
            title="Treatment readiness"
          />
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recommendation</div>
          <select disabled={!editable} value={rec} onChange={e => setRec(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="doctor-review-recommendation">
            {REC_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        {[
          ['Recommended treatments', recommended, setRecommended, 'doctor-review-recommended'],
          ['Additional treatments',  additional,  setAdditional,  'doctor-review-additional'],
          ['Alternative treatments', alternative, setAlternative, 'doctor-review-alternative'],
          ['Removed / not advised',  removed,     setRemoved,     'doctor-review-removed'],
        ].map(([label, val, setVal, tid]) => (
          <TreatmentPicker
            key={tid}
            label={label}
            value={val}
            onChange={setVal}
            catalog={catalog}
            disabled={!editable}
            testIdPrefix={tid}
          />
        ))}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Patient-visible summary</div>
          <textarea disabled={!editable} rows={3} value={patientSummary} onChange={e => setPatientSummary(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="doctor-review-patient-summary" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sales rep guidance (internal)</div>
          <textarea disabled={!editable} rows={2} value={guidance} onChange={e => setGuidance(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="doctor-review-sales-guidance" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Internal doctor notes</div>
          <textarea disabled={!editable} rows={3} value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="doctor-review-notes" />
        </div>
      </div>
      <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
        {editable ? (
          <button onClick={handleSubmit} disabled={submitting} className="px-4 py-1.5 bg-slate-900 text-white text-sm rounded disabled:opacity-50" data-testid="doctor-review-submit-btn">
            {submitting ? 'Submitting…' : 'Submit review'}
          </button>
        ) : (
          <span className="text-xs text-slate-500" data-testid="doctor-review-readonly">Read-only — review already {review.status}.</span>
        )}
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------- */
/*  M16B-06B — Canonical Treatment Picker                                */
/*                                                                       */
/*  Searches the canonical treatment_content catalog (no manual slug     */
/*  entry, no free-text). The component is purely a selector over the    */
/*  existing SSOT — it does NOT create a new treatment vocabulary.       */
/* -------------------------------------------------------------------- */
function TreatmentPicker({ label, value, onChange, catalog, disabled, testIdPrefix }) {
  const [query, setQuery] = useState('');
  const [open,  setOpen]  = useState(false);

  const selectedSet = new Set(value);
  const slugToRow = Object.fromEntries(catalog.map(r => [r.slug, r]));

  const filtered = !open ? [] : catalog.filter(r => {
    if (selectedSet.has(r.slug)) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.name || '').toLowerCase().includes(q) ||
      (r.slug || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q)
    );
  }).slice(0, 30);

  const add = (slug) => {
    if (!selectedSet.has(slug)) onChange([...value, slug]);
    setQuery('');
  };
  const remove = (slug) => onChange(value.filter(s => s !== slug));

  return (
    <div data-testid={testIdPrefix}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="border border-slate-300 rounded px-2 py-1.5 flex flex-wrap gap-1 min-h-[34px] bg-white">
        {value.map(slug => (
          <span key={slug} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-800 rounded px-1.5 py-0.5" data-testid={`${testIdPrefix}-chip-${slug}`}>
            {slugToRow[slug]?.name || slug}
            {!disabled && (
              <button type="button" onClick={() => remove(slug)} className="text-slate-500 hover:text-rose-600" data-testid={`${testIdPrefix}-remove-${slug}`}>×</button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            value={query}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            placeholder={value.length === 0 ? 'Search canonical treatments…' : 'Add another…'}
            className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
            data-testid={`${testIdPrefix}-input`}
          />
        )}
      </div>
      {!disabled && open && filtered.length > 0 && (
        <div className="mt-1 border border-slate-200 rounded bg-white shadow max-h-44 overflow-y-auto" data-testid={`${testIdPrefix}-dropdown`}>
          {filtered.map(r => (
            <button
              key={r.slug}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); add(r.slug); }}
              className="w-full text-left px-2 py-1 text-sm hover:bg-slate-50 flex justify-between items-center"
              data-testid={`${testIdPrefix}-option-${r.slug}`}
            >
              <span className="text-slate-800">{r.name}</span>
              <span className="text-[10px] font-mono text-slate-400">{r.category || ''}</span>
            </button>
          ))}
        </div>
      )}
      {!disabled && open && filtered.length === 0 && query.trim() && (
        <div className="mt-1 text-[11px] text-slate-500 italic px-2" data-testid={`${testIdPrefix}-no-match`}>
          No canonical treatment matches "{query}". Manual entry is not allowed.
        </div>
      )}
    </div>
  );
}
